import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Heart, Timer, Users, ShieldCheck, Weight, Check, Truck } from "lucide-react";
import { useProduct, inr, pctOff, buildLabel, CATEGORY_NAMES } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Stars } from "../components/Stars";
import { QtyStepper } from "../components/CartDrawer";
import { PincodeChecker } from "../components/PincodeChecker";
import { Reviews } from "../components/Reviews";
import { ProductCard } from "../components/ProductCard";
import { Skeleton } from "../components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";

const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [images]);
  return (
    <div className="flex flex-col gap-3 lg:sticky lg:top-32">
      <div className="aspect-square overflow-hidden rounded-md bg-jute/40">
        <img data-testid="product-main-image" src={images[i]} alt={name} className="h-full w-full object-cover" />
      </div>
      <div className="flex gap-3">
        {images.map((src, idx) => (
          <button key={src} data-testid={`product-thumb-${idx}`} onClick={() => setI(idx)} className={`h-20 w-20 overflow-hidden rounded border-2 transition-colors ${idx === i ? "border-sheesham" : "border-transparent opacity-70 hover:opacity-100"}`}>
            <img src={src} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const VariantPicker = ({ variants, value, onChange }) => (
  <div data-testid="variant-picker">
    <p className="text-sm font-medium">Finish: <span data-testid="variant-selected-name">{variants.find((v) => v.key === value)?.name}</span></p>
    <div className="mt-3 flex flex-wrap gap-3">
      {variants.map((v) => (
        <button
          key={v.key} data-testid={`variant-option-${v.key}`} onClick={() => onChange(v.key)} aria-pressed={v.key === value}
          className={`flex items-center gap-2.5 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-medium transition-colors ${v.key === value ? "border-foreground bg-card" : "border-border hover:border-foreground/40"}`}
        >
          <span style={{ backgroundColor: v.swatch }} className={`h-7 w-7 rounded-full ${v.key === value ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : ""}`} />
          {v.name}
        </button>
      ))}
    </div>
  </div>
);

const Addons = ({ addons, selected, toggle }) => (
  <div data-testid="addons-section" className="rounded-md border border-border p-5">
    <p className="text-sm font-semibold">Fits with it</p>
    <ul className="mt-3 space-y-3">
      {addons.map((a) => (
        <li key={a.slug} className="flex items-center gap-3">
          <Checkbox id={`addon-${a.slug}`} data-testid={`addon-checkbox-${a.slug}`} checked={selected.includes(a.slug)} onCheckedChange={() => toggle(a.slug)} />
          <img src={a.images[0]} alt="" className="h-12 w-12 rounded object-cover" />
          <label htmlFor={`addon-${a.slug}`} className="flex-1 cursor-pointer text-sm">
            <span className="font-medium">{a.name}</span>
            <span className="block text-xs text-muted-foreground">{buildLabel(a.build_minutes)}</span>
          </label>
          <span className="text-sm font-semibold">+{inr(a.price)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Facts = ({ p }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {[[Timer, buildLabel(p.build_minutes)], [Users, p.people === 1 ? "1 person" : `${p.people} people`], [ShieldCheck, `${p.warranty_years}-year warranty`], [Weight, `${p.weight_kg} kg box`]].map(([I, t]) => (
      <div key={t} className="flex flex-col gap-2 rounded-md bg-muted p-3 text-sm"><I className="h-4 w-4 text-sheesham dark:text-brass" />{t}</div>
    ))}
  </div>
);

const Details = ({ p }) => (
  <Accordion type="multiple" defaultValue={["box"]} className="border-t border-border">
    <AccordionItem value="box">
      <AccordionTrigger data-testid="accordion-in-box" className="text-base font-semibold hover:no-underline">What's in the box</AccordionTrigger>
      <AccordionContent>
        <ul data-testid="in-box-list" className="space-y-2">
          {p.in_box.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neem dark:text-jute" />{x}</li>)}
        </ul>
      </AccordionContent>
    </AccordionItem>
    <AccordionItem value="spec">
      <AccordionTrigger data-testid="accordion-specs" className="text-base font-semibold hover:no-underline">Size and material</AccordionTrigger>
      <AccordionContent className="space-y-2 text-sm">
        <p><span className="text-muted-foreground">Dimensions:</span> {p.dimensions}</p>
        <p><span className="text-muted-foreground">Material:</span> {p.material}</p>
      </AccordionContent>
    </AccordionItem>
    <AccordionItem value="delivery">
      <AccordionTrigger data-testid="accordion-delivery" className="text-base font-semibold hover:no-underline">Delivery, payment and returns</AccordionTrigger>
      <AccordionContent className="space-y-2 text-sm text-muted-foreground">
        <p>Free delivery in 1 to 2 days, anywhere in Hyderabad and Secunderabad. We deliver only within Hyderabad for now.</p>
        <p>Ships in one flat box that fits in any lift.</p>
        <p>Pay by cash or UPI when the box arrives.</p>
        <p>Returns within 10 days if unbuilt or faulty. Missing parts sent free, within 2 working days in Hyderabad.</p>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useProduct(slug);
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState([]);
  const [sp, setSp] = useSearchParams();
  useEffect(() => { setQty(1); setSelected([]); }, [slug]);

  if (isLoading) return <div className="container-hw grid gap-10 py-12 lg:grid-cols-2"><Skeleton className="aspect-square" /><Skeleton className="h-96" /></div>;
  if (isError || !data) return <div className="container-hw py-24 text-center"><h1 className="text-4xl">We couldn't find that piece.</h1><Link to="/shop" className="btn-sheesham mt-8">Back to shop</Link></div>;

  const { product: p, addons, related } = data;
  const variant = p.variants.find((v) => v.key === sp.get("finish")) || p.variants[0];
  const images = variant?.images || p.images;
  const chosen = addons.filter((a) => selected.includes(a.slug));
  const total = p.price * qty + chosen.reduce((s, a) => s + a.price, 0);
  const toggle = (s) => setSelected((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]));
  const add = () => addToCart([{ ...p, qty, variant: variant?.key }, ...chosen]);
  const saved = inWishlist(p.slug);
  const pickVariant = (k) => setSp((prev) => { const n = new URLSearchParams(prev); n.set("finish", k); return n; }, { replace: true });

  return (
    <div className="pb-24 lg:pb-0">
      <div className="container-hw grid gap-10 py-10 lg:grid-cols-12 lg:gap-16 lg:py-14">
        <div className="lg:col-span-7"><Gallery images={images} name={variant ? `${p.name} in ${variant.name}` : p.name} /></div>
        <div className="space-y-6 lg:col-span-5">
          <nav className="text-xs text-muted-foreground"><Link to="/shop" className="hover:underline">Shop</Link> / <Link to={`/shop/${p.category}`} className="hover:underline">{CATEGORY_NAMES[p.category]}</Link></nav>
          <div>
            {p.badge === "New" && <p className="eyebrow mb-3 text-brass">New in · {CATEGORY_NAMES[p.category]}</p>}
            <h1 data-testid="product-title" className="text-4xl sm:text-5xl">{p.name}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{p.tagline}</p>
            <p data-testid="product-spec-line" className="spec-line mt-3">{[p.dimensions.replace("Approx. ", ""), `${p.weight_kg} kg`, p.build_minutes ? `${p.build_minutes} min build` : null].filter(Boolean).join(" · ")}</p>
            <a href="#reviews" className="mt-3 inline-flex items-center gap-2 text-sm"><Stars value={p.rating} /> {p.review_count ? `${p.rating} (${p.review_count} reviews)` : "No reviews yet"}</a>
          </div>
          <div>
            <div className="flex flex-wrap items-baseline gap-3">
              <span data-testid="product-detail-price" className="price text-4xl">{inr(p.price)}</span>
              {pctOff(p) > 0 && <span className="mrp text-lg">{inr(p.mrp)}</span>}
              {pctOff(p) > 0 && <span className="rounded-full bg-sheesham px-2.5 py-1 text-xs font-semibold text-linen">Save {inr(p.mrp - p.price)} ({pctOff(p)}%)</span>}
            </div>
            <p data-testid="product-tax-note" className="small-print mt-2">
              {p.gst_exclusive ? `Excluding tax (${p.gst_rate}% GST, ${inr(Math.round((p.price * p.gst_rate) / 100))}, added at checkout).` : "Inclusive of all taxes."} Free delivery, 1 to 2 days in Hyderabad.
            </p>
          </div>
          {variant && <VariantPicker variants={p.variants} value={variant.key} onChange={pickVariant} />}
          {p.features.length > 0 && (
            <ul data-testid="product-features" className="grid gap-3 border-y border-border py-4 text-sm text-muted-foreground sm:grid-cols-3">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neem dark:text-jute" />{f}</li>)}
            </ul>
          )}
          <Facts p={p} />
          {addons.length > 0 && <Addons addons={addons} selected={selected} toggle={toggle} />}
          <div className="flex items-center gap-3">
            <QtyStepper qty={qty} onChange={(q) => setQty(Math.max(1, Math.min(10, q)))} testid="product-qty" />
            <button data-testid="add-to-cart-button" onClick={add} className="btn-sheesham flex-1 py-3.5">
              Add to cart{chosen.length > 0 && ` with ${chosen.length} add-on${chosen.length > 1 ? "s" : ""}`} · {inr(total)}
            </button>
            <button data-testid="product-wishlist-button" onClick={() => toggleWishlist(p)} aria-label="Save to wishlist" className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-border hover:border-foreground/40">
              <Heart className={`h-5 w-5 ${saved ? "fill-sheesham text-sheesham" : ""}`} />
            </button>
          </div>
          <PincodeChecker />
          <p className="leading-relaxed">{p.description}</p>
          <Details p={p} />
        </div>
      </div>
      <div className="container-hw border-t border-border py-16"><Reviews product={p} /></div>
      {related.length > 0 && (
        <section className="container-hw py-16">
          <h2 className="text-3xl">More for the {CATEGORY_NAMES[p.category].toLowerCase()}</h2>
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-12 lg:grid-cols-4">{related.map((r) => <ProductCard key={r.slug} product={r} />)}</div>
        </section>
      )}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-border bg-background/95 px-5 py-3 backdrop-blur lg:hidden">
        <div><p className="text-xs text-muted-foreground">{p.name}</p><p className="font-semibold">{inr(total)}</p></div>
        <button data-testid="mobile-add-to-cart-button" onClick={add} className="btn-sheesham"><Truck className="h-4 w-4" />Add to cart</button>
      </div>
    </div>
  );
}
